/**
 * Off-cache enrichment — the client half of the scoring backend.
 *
 * Three modes, selected by `window.ATLAS_BACKEND_URL` (set inline in index.html):
 *   - a path/URL → POST there. This is production: the Worker holds the API key.
 *   - `"direct"`  → call Anthropic from the browser. Only valid inside a Claude
 *                   artifact runtime, which injects auth; there is no key in the page.
 *   - `""`        → no backend. Enrichment UI renders disabled.
 */
import { ATLAS_SYSTEM_PROMPT } from '../core/prompt.ts';
import type { Asset, BackendMode } from '../core/types.ts';

declare global {
  interface Window {
    ATLAS_BACKEND_URL?: string;
  }
}

/**
 * Model for direct mode.
 *
 * The legacy build hardcoded `claude-sonnet-4-20250514` here while the PHP proxy used
 * `claude-haiku-4-5-20251001`, so the two paths scored differently despite the README
 * saying otherwise. Aligned to the proxy default; the Worker's ATLAS_MODEL still
 * overrides for proxy mode.
 */
const DIRECT_MODEL = 'claude-haiku-4-5-20251001';
const DIRECT_MAX_TOKENS = 8192;

export function resolveBackendMode(raw: string | undefined = window.ATLAS_BACKEND_URL): BackendMode {
  const value = (raw ?? '').trim();
  if (!value) return { kind: 'none' };
  if (value === 'direct') return { kind: 'direct' };
  return { kind: 'proxy', url: value };
}

/** Is live enrichment available at all? Drives the enabled state of enrichment buttons. */
export function backendAvailable(mode: BackendMode = resolveBackendMode()): boolean {
  return mode.kind !== 'none';
}

/** Extract the scorecard object from model text that may carry fences or prose. */
export function parseScorecardText(text: string): Asset | null {
  let s = text.replace(/^```(?:json)?\s*/gm, '').replace(/\s*```\s*$/gm, '').trim();
  const first = s.indexOf('{');
  const last = s.lastIndexOf('}');
  if (first !== -1 && last !== -1 && last > first) s = s.slice(first, last + 1);
  try {
    const parsed = JSON.parse(s) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
    return parsed as Asset;
  } catch {
    return null;
  }
}

async function viaProxy(url: string, query: string): Promise<Asset | null> {
  try {
    const resp = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query }),
    });
    if (!resp.ok) throw new Error(`Backend status ${resp.status}`);
    const data = (await resp.json()) as Asset & { error?: string };
    if (data.error) throw new Error(data.error);
    return data;
  } catch (err) {
    console.error('Atlas proxy backend failed:', err);
    return null;
  }
}

async function viaDirect(query: string): Promise<Asset | null> {
  try {
    const resp = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: DIRECT_MODEL,
        max_tokens: DIRECT_MAX_TOKENS,
        system: ATLAS_SYSTEM_PROMPT,
        tools: [{ type: 'web_search_20250305', name: 'web_search' }],
        messages: [{ role: 'user', content: query }],
      }),
    });
    if (!resp.ok) {
      const errText = await resp.text().catch(() => '');
      throw new Error(`Anthropic API status ${resp.status}: ${errText.slice(0, 200)}`);
    }
    const data = (await resp.json()) as { content?: Array<{ type?: string; text?: string }> };
    // Last text block wins — tool_use blocks are interleaved before it.
    let text = '';
    for (const block of data.content ?? []) {
      if (block?.type === 'text' && typeof block.text === 'string') text = block.text;
    }
    if (!text) {
      console.error('Atlas direct API: no text block in response', data);
      return null;
    }
    return parseScorecardText(text);
  } catch (err) {
    console.error('Atlas direct API failed:', err);
    return null;
  }
}

/** Score an off-cache query. Resolves to `null` on any failure; callers surface a retry. */
export async function enrichFromBackend(
  query: string,
  mode: BackendMode = resolveBackendMode(),
): Promise<Asset | null> {
  switch (mode.kind) {
    case 'none':
      return null;
    case 'direct':
      return viaDirect(query);
    case 'proxy':
      return viaProxy(mode.url, query);
  }
}
